import React, { createContext, useCallback, useMemo, useState } from 'react'

import { Modal } from 'antd'
import { useSelector } from 'react-redux'
import { isFunction } from 'lodash'

import InstitutionForm, { Modes } from './InstitutionForm'
import { ModalTypes } from '../IndividualInternshipRequest/IndividualInternshipRequestModal'
import { isObject, validateString } from '../../../utils'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import InstitutionValidator from './InstitutionValidator'
import useInstitution from '../../../utils/api/hooks/useInstitution'
import StudentInstitutionForm from './StudentInstitutionForm'
import SectorValidator from './SectorValidators'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { createSector } from '../../../utils/api/sector'
import { Sector } from '../../../utils/entities/sector'

export const InstitutionModalContext = createContext()

const DEFAULT_HANDLER = { data: null, callback: null, type: ModalTypes.FORM }

const InstitutionModal = ({ children }) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const institutionHandler = useInstitution({ user, t })

  const [formHandler, setFormHandler] = useState(DEFAULT_HANDLER)
  const [selectedInstitution, setSelectedInstitution] = useState(null)

  const closeModal = useCallback(() => {
    setFormHandler(DEFAULT_HANDLER)
    setSelectedInstitution(null)
  }, [])

  /** The sector step is reached either after a merge, or straight away for a place the school already knows. */
  const sectorInstitution = selectedInstitution ?? formHandler.data

  const handleUpsertInstitution = useCallback(institution => {
    institutionHandler.upsert(institution).then(newInstitution => {
      if (isFunction(formHandler.callback)) {
        formHandler.callback(newInstitution)
      }

      closeModal()
    })
  }, [formHandler.callback, institutionHandler.upsert, closeModal])

  const handleCreateSector = useCallback((sector, institution) => {
    createSector(user, new Sector({ ...sector, institution }).toRequestBody()).then(({ data }) => {
      if (isFunction(formHandler.callback) && isObject(data)) {
        formHandler.callback(institution, data)
      }

      closeModal()
      onSuccess(t('institution.create_sector.success', { sectorName: data.name }))
    }).catch(error => {
      console.error(error)
      onError(t('institution.create_sector.error'))
    })
  }, [formHandler.callback, user, t, closeModal])

  const handleCreateInstitution = useCallback(() => {
    const institution = formHandler.data.toRequestBody()
    const sector = formHandler.data.sector

    delete institution.sector

    institutionHandler.create(institution).then(newInstitution => {
      if (!isObject(newInstitution)) {
        return
      }

      /** A student only describes the place itself : there is no service to create out of a name he never gave. */
      if (validateString(sector?.name, { notEmpty: true }).valid) {
        handleCreateSector(sector, newInstitution)
      } else {
        if (isFunction(formHandler.callback)) {
          formHandler.callback(newInstitution)
        }

        closeModal()
      }
    })
  }, [formHandler.data, formHandler.callback, institutionHandler.create, handleCreateSector, closeModal])

  const handleSubmit = institution => {
    if (isFunction(formHandler.callback)) {
      formHandler.callback(institution)
    }

    closeModal()
  }

  const handlerMergeInstitution = useCallback(newInstitution => {
    setSelectedInstitution(newInstitution)
    setFormHandler(form => ({ ...form, type: ModalTypes.SECTOR_VALIDATION }))
  }, [])

  const handleMergeSector = useCallback(newSector => {
    formHandler.callback(sectorInstitution, newSector)
    closeModal()
  }, [formHandler.callback, sectorInstitution, closeModal])

  /** No service was ever proposed : the merged place alone is then all the request has to be linked with. */
  const handleLinkInstitution = useCallback(() => {
    if (isFunction(formHandler.callback)) {
      formHandler.callback(sectorInstitution)
    }

    closeModal()
  }, [formHandler.callback, sectorInstitution, closeModal])

  const isDataLoaded = isObject(formHandler?.data)

  const context = useMemo(() => ({ setFormHandler }), [])

  return (
    <>
      <InstitutionModalContext.Provider value={context}>
        {children}
      </InstitutionModalContext.Provider>
      <Modal
        visible={isDataLoaded}
        width={formHandler.type === ModalTypes.INSTITUTION_VALIDATION || formHandler.type === ModalTypes.SECTOR_VALIDATION ? 600 : 540}
        onCancel={closeModal}
        title={t(`institution_modal.title.${formHandler.type.toLowerCase()}`)}
        footer={null}
      >
        {isDataLoaded && formHandler.type === ModalTypes.FORM && (
          <InstitutionForm data={formHandler.data} onSubmit={handleUpsertInstitution} mode={Modes.LIGHT} />
        )}
        {isDataLoaded && formHandler.type === ModalTypes.INSTITUTION_VALIDATION && (
          <InstitutionValidator institution={formHandler.data} onValidate={handleCreateInstitution} onMerge={handlerMergeInstitution} />
        )}
        {isDataLoaded && formHandler.type === ModalTypes.STUDENT && (
          <StudentInstitutionForm data={formHandler.data} onSubmit={handleSubmit} />
        )}
        {isDataLoaded && formHandler.type === ModalTypes.SECTOR_VALIDATION && (
          <SectorValidator
            sector={formHandler.data.sector}
            institution={sectorInstitution}
            onValidate={sector => handleCreateSector(sector, sectorInstitution)}
            onMerge={handleMergeSector}
            onSkip={handleLinkInstitution}
          />
        )}
      </Modal>
    </>
  )
}

export default InstitutionModal
